import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement } from "@stripe/stripe-js";
import { Button, Col, Row } from "antd";
import Checkbox from "antd/es/checkbox";
import Search from "antd/es/input/Search";
import Layout from "antd/lib/layout/layout";
import React, { useCallback, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { batch, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { RootState } from "src";
import {
  clearSameDaySelection,
  setDate,
  setTime,
  setPaymentIntent,
  setPriceNotice,
  setPromocode,
  setProximity,
  setZip,
} from "src/actions";
import { forgetAreaCalendar } from "src/hooks/useAreaCalendar";
import { HttpError } from "src/types/api";
import { areaTierFor, computeBookingPrice } from "src/lib/price";
import { quoteFor, quoteKey } from "src/lib/proximity";
import { PriceNotice } from "./utils/PriceNotice";
import { logEvent } from "src/analytics/amplitude";
import { useConfirmation } from "src/context/confirmation";
import { VerifyCoupon } from "src/graphql/mutations/coupons";
import { useResizeObserver } from "src/hooks/useResizeObserver";
import { contactOptions } from "src/pages/StepFour";
import { openNotificationWithIcon } from "src/components/utils/OpenNotificationWithIcon";
import { BookingInput } from "src/types";
import * as Sentry from "@sentry/react";
import {
  loadPricingConfig,
  pricingConfigLoadFailed,
  pricingConfigVersion,
} from "src/lib/pricing-config";
import { userSchema } from "src/types/userSchema";
import { debounce, formatToPhone } from "src/weirdStuff";
import {
  createPaymentIntent,
  getBookingDetails,
  updatePaymentIntent,
  verifyPayment,
} from "src/weirdStuff/api";
import { useMutation } from "urql";
import { CheckoutForm } from "./utils/CheckoutForm";
import FormInput from "./utils/FormInput";
import { StyledRadioGroup } from "./utils/RadioGroup";
import Tip from "./utils/Tip";

const Payments = (props) => {
  const [status, setStatus] = React.useState("idle");
  const state = useSelector((state: RootState) => state.bookingReducer);
  const dispatch = useDispatch();

  const width = useResizeObserver();

  const { code } = state;

  const stripe = useStripe();
  const elements = useElements();

  const [, verifyCoupon] = useMutation(VerifyCoupon);

  const { confirmBooking } = useConfirmation();

  const navigate = useNavigate();

  const { control, formState, handleSubmit, watch } = useFormContext();

  const watchPaymentType = watch("paymentType", "stripe");
  const watchEmail = watch("email");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkForCredits = useCallback(
    debounce(
      (email: string) => state.occurance === 0 && props.checkEmail(email),
      3000
    ),
    []
  );

  useEffect(() => {
    if (watchEmail && !formState.errors["email"]) {
      checkForCredits(watchEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkForCredits, formState.errors?.email, watchEmail]);

  const isCouponValid = (code: string) => {
    setStatus("verifying coupon");
    if (state.occurance > 0) {
      logEvent("Coupon code not used since frequency is greater than once.");
      openNotificationWithIcon(
        "error",
        "Cannot apply coupons",
        "Coupon codes are not valid for weekly, bi-weekly or monthly occurrence"
      );
      dispatch(setPromocode("", 0));
      setStatus("idle");
      return;
    }
    verifyCoupon({ code, email: watchEmail }).then((result) => {
      setStatus("idle");
      if (result.error || !result.data) {
        logEvent("User entered an invalid promocode", { promocode: code });
        openNotificationWithIcon("error", "Invalid promocode", "");
        dispatch(setPromocode("", 0));
      } else {
        logEvent("User applied promocode", { promocode: code });
        openNotificationWithIcon(
          "success",
          "Promocode applied",
          "You just availed a discount of $" +
            result.data.verifyCoupon?.discount +
            "!"
        );
        dispatch(setPromocode(code, result.data?.verifyCoupon?.discount));
      }
    });
  };

  /**
   * The server refused the total with 409: the price moved after it was shown.
   * Nothing has been charged.
   *
   * The 409 says what the server priced — the ZIP the address is in, the area
   * tier, and a token for exactly that — and that is taken as the answer. The
   * app used to re-ask about its own ZIP, and when the server had priced a
   * different one (an address between two ZIPs, a quote that had failed) the
   * answer never moved and every Book was refused again. The rates are reloaded
   * too, in case an admin changed them.
   *
   * Then one notice, never "refresh the page" (which lost everything typed):
   * the new total with the reason; or, when the total did not move, that it was
   * checked and they can book; or, when the app still cannot reach the server's
   * number, a way back to pick the date and time again.
   */
  async function requote(refusal: HttpError) {
    const wasTotalCents = computeBookingPrice(state).totalCents;
    const tierBefore = areaTierFor(state);
    const versionBefore = pricingConfigVersion();
    const priced = refusal.priced;

    await loadPricingConfig(true).catch(() => undefined);

    let after = state;
    if (priced) {
      const zip = priced.zip ?? "";
      const key = quoteKey({ ...state, zip });
      const adopted = { tier: priced.tier, percent: priced.percent, token: priced.token, key };
      // One render for both: dispatched apart, the ZIP's render made the quote
      // hook see a new question and wipe the tier just adopted.
      batch(() => {
        if (zip !== state.zip) dispatch(setZip(zip));
        dispatch(setProximity(adopted));
      });
      after = {
        ...state,
        zip,
        proximityTier: adopted.tier,
        proximityPercent: adopted.percent,
        proximityToken: adopted.token,
        proximityFor: key,
      };
    } else {
      const quote = await quoteFor(state).catch(() => null);
      if (quote) {
        dispatch(setProximity(quote));
        after = {
          ...state,
          proximityTier: quote.tier,
          proximityPercent: quote.percent,
          proximityToken: quote.token,
          proximityFor: quote.key,
        };
      }
    }

    const totalAfter = computeBookingPrice(after).totalCents;
    const tierAfter = areaTierFor(after);
    if (tierBefore === "cluster" && tierAfter !== "cluster") forgetAreaCalendar(state.zip);

    if (priced && totalAfter !== priced.totalCents) {
      dispatch(setPriceNotice({ reason: "mismatch", wasTotalCents }));
      return;
    }
    dispatch(
      setPriceNotice({
        reason:
          totalAfter === wasTotalCents
            ? "checked"
            : pricingConfigVersion() !== versionBefore
            ? "prices"
            : tierBefore === "cluster" && tierAfter !== "cluster"
            ? "gone"
            : tierAfter === "travel" && tierBefore !== "travel"
            ? "fee"
            : "changed",
        wasTotalCents,
      })
    );
  }

  async function onSubmit(data: typeof userSchema.__inputType) {
    if (!data.terms) return;
    // Booking again is the answer to a notice about the price; it goes.
    if (state.priceNotice) dispatch(setPriceNotice(null));

    /**
     * Nothing is charged while we are quoting from the bundled fallback.
     *
     * When `/pricing/config` fails, this app keeps showing prices — the ones
     * compiled into the bundle, with no surge, no Same-Day and whatever base
     * rates were current when it was built. That is fine for browsing and
     * catastrophic at checkout: the server prices from *its* config, Stripe
     * captures that amount, and verify then rejects the booking for disagreeing
     * with the total this app posted. The customer is charged and has no
     * booking. The server now refuses before charging (see
     * `assertQuoteStillValid`), but that is a backstop; a customer should not
     * reach a payment form we already know is showing the wrong number.
     *
     * One more attempt first, because the failure may be minutes old and a
     * single retry is cheaper than sending someone away.
     */
    if (pricingConfigLoadFailed()) {
      await loadPricingConfig().catch(() => undefined);
    }
    if (pricingConfigLoadFailed()) {
      Sentry.captureMessage(
        "Checkout blocked: pricing config unavailable",
        Sentry.Severity.Error
      );
      return openNotificationWithIcon(
        "error",
        "We can't take this booking right now",
        "We're having trouble loading our current prices, so we can't confirm " +
          "your total. Nothing has been charged. Please refresh the page, or " +
          "try again in a few minutes."
      );
    }

    setStatus("booking");

    const booking: BookingInput = getBookingDetails({ state, ...data });

    /**
     * The rates this quote came from, echoed so the server can tell whether an
     * admin changed a price while this tab was open.
     */
    booking.pricing_config_version = pricingConfigVersion() ?? undefined;

    if (data.tipAmount || data.tipOptions) {
      if (data.tipOptions === "other") {
        booking.tip = data.tipAmount;
      } else {
        booking.tip = Number(data.tipOptions);
      }
    }

    if (
      data.paymentType === "stripe" &&
      booking.total_amount! > state.user.credits
    ) {
      const cardElement = elements?.getElement(
        CardElement
      ) as StripeCardElement;
      const pm = await stripe?.createPaymentMethod({
        billing_details: {
          name: data.name,
          email: data.email,
          phone: data.phone,
        },
        type: "card",
        card: cardElement,
      });
      if (pm?.error) {
        setStatus("idle");
        return openNotificationWithIcon(
          "error",
          "Error",
          pm.error?.message ?? "Failed to validate card details"
        );
      } else {
        booking.payment_method_id = pm?.paymentMethod.id;
      }
    }

    // if we have a previously saved payment_intent, we might want to verify if it's upto date,
    // if not, we'll create a new payment_intent
    const paymentIntent = state.paymentIntent
      ? await updatePaymentIntent(
          state.paymentIntent,
          booking,
          state.user.token
        ).catch(() => setStatus("idle"))
      : await createPaymentIntent(booking, state.user.token).catch(() =>
          setStatus("idle")
        );

    if (paymentIntent && "error" in paymentIntent && paymentIntent.status === 409) {
      logEvent("Checkout re-quoted", { error: paymentIntent.error as string });
      await requote(paymentIntent as HttpError);
      setStatus("idle");
      return;
    }

    if (!paymentIntent || "error" in paymentIntent) {
      console.error(paymentIntent?.error);
      logEvent("Payment setup failed", {
        user: data.fullname,
        error: paymentIntent?.error as string,
      });

      /**
       * A message, always.
       *
       * `createPaymentIntent(...).catch(() => setStatus("idle"))` resolves to
       * `undefined` on a network failure, so this used to render a notification
       * titled "Failed to complete booking" with an empty body — the customer
       * was told something went wrong and nothing about what, including whether
       * they had been charged.
       */
      const serverMessage = paymentIntent?.error as string | undefined;
      const description =
        serverMessage && serverMessage.trim()
          ? serverMessage
          : "We couldn't reach our booking service. Nothing has been charged — " +
            "please check your connection and try again.";

      /**
       * A Same-Day window that closed while the customer was filling in their
       * card is not a dead end.
       *
       * The server refuses with "choose another window", which was shown as a
       * toast on a screen that has no window chooser on it: clicking Book again
       * failed identically, and the only way out was to guess at Back twice.
       * Sending them to the date step puts them in front of the control the
       * message is asking them to use, with the stale selection cleared so the
       * step re-asks the server for what is still open.
       */
      /**
       * The same, for a slot or day an admin blocked after the customer chose
       * it (`lib/schedule-guard.ts` on the API). Nothing has been charged yet.
       * The time is cleared so the step makes them choose again; a day that is
       * gone takes the date with it, and the step moves them to the next open one.
       */
      if (serverMessage && /no longer available/i.test(serverMessage)) {
        batch(() => {
          if (/this day/i.test(serverMessage)) dispatch(setDate(""));
          dispatch(setTime(""));
        });
        openNotificationWithIcon("error", "That time is no longer available", description);
        setStatus("idle");
        props.changePage?.(2);
        return;
      }

      if (serverMessage && /arrival window|Same-Day/i.test(serverMessage)) {
        dispatch(clearSameDaySelection());
        openNotificationWithIcon("error", "That time is no longer available", description);
        setStatus("idle");
        props.changePage?.(2);
        return;
      }

      openNotificationWithIcon("error", "Failed to complete booking", description);
      setStatus("idle");
      return;
    }

    // store the updated payment intent in the redux store
    dispatch(setPaymentIntent(paymentIntent));

    // if the payment type is "cash", we'll just confirm the booking
    if (
      data.paymentType === "cash" ||
      (booking.frequency === "One Time" &&
        booking.total_amount! <= state.user.credits)
    ) {
      const result = await verifyPayment({
        booking,
        payment_intent: paymentIntent.id,
        payment_type: "cash",
        token: state.user.token,
      });
      if ("error" in result) {
        setStatus("idle");
        openNotificationWithIcon(
          "error",
          "Payment failed",
          (result.error as string) || ""
        );
        return;
      }
      if (result.data?.total_amount) {
        // if the booking is successful, we'll send the user to the confirmation page
        confirmBooking({
          email: result.data.email,
          fullname: result.data.fullname,
          clean_date: result.data.clean_date,
          total_amount: result.data.total_amount,
          bookingId: result.data.bookingId || "",
          instructionsToken: result.data.instructionsToken || "",
          source: "bookings",
        });
        navigate("/confirmation", { replace: true });
        return;
      }
      setStatus("idle");
      return openNotificationWithIcon(
        "error",
        "Payment failed",
        "Something went wrong"
      );
    }

    // For one-time booking, if payment status is 'requires_action' or
    // 'requires_payment_method', we'll need to confirm the card payment.
    if (
      paymentIntent.status === "requires_action" ||
      paymentIntent.status === "requires_payment_method" ||
      paymentIntent.status === "requires_confirmation"
    ) {
      const result = await stripe?.confirmCardPayment(
        paymentIntent.client_secret!,
        {
          payment_method: booking.payment_method_id,
        }
      );
      if (result?.error) {
        setStatus("idle");
        openNotificationWithIcon(
          "error",
          "Payment failed",
          result?.error?.message ?? "Something went wrong"
        );
        return;
      }
    }

    setStatus("idle");

    logEvent("Payment successful", {
      user: data.fullname,
      total_amount: booking.total_amount,
      clean_date: booking.clean_date,
    });
    confirmBooking({
      fullname: data.fullname as string,
      email: data.email as string,
      clean_date: booking.clean_date,
      total_amount: booking.total_amount as number,
      source: "bookings",
    });
    navigate("/payments/verify", {
      state: {
        paymentMethod: booking.payment_method_id,
        paymentIntent: paymentIntent.id,
      },
      replace: true,
    });
  }

  return (
    <Layout className="step-layout">
      <h1 className="semi-bold">Alright, Let's get this wrapped up.</h1>
      <p>Now just enter your contact and payment details for Homero.</p>
      <h3 className="mt-2 semi-bold" style={{ marginBottom: 2 }}>
        Your Details
      </h3>
      {/* @ts-ignore */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row
          gutter={{ xs: 0, md: 16 }}
          style={{
            marginTop: 5,
          }}
        >
          <Col xs={24} md={12}>
            <FormInput label="Full Name" id="fullname" type="text" />
          </Col>
          <Col xs={24} md={12}>
            <FormInput label="Email" id="email" type="email" />
          </Col>
          <Col xs={24} lg={12}>
            <FormInput
              label="Phone Number"
              autoComplete="tel"
              prefix="+1"
              id="phone"
              type="tel"
              onChange={(e) => formatToPhone(e)}
            />
          </Col>
          <Col xs={24} lg={12}>
            <StyledRadioGroup
              id="contactType"
              label="Contact Type"
              size="small"
              options={contactOptions}
            />
          </Col>
        </Row>
        <br />
        <br />
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <label className="mt-2">Promo Code</label>
            <Search
              enterButton={
                status === "verifying coupon"
                  ? "Applying coupon..."
                  : "Apply coupon"
              }
              defaultValue={code}
              onSearch={(value) => isCouponValid(value)}
              className="input"
              type="search"
              style={{ width: "100%", borderRadius: 6, border: "none" }}
              placeholder="Enter your promo code to avail discount"
              size="large"
            />
          </Col>
          <Col lg={12} xs={24}>
            <Tip />
          </Col>
        </Row>
        <br />
        {state.user.credits < state.totalPrice && (
          <>
            <h3 className="mt-2 mb-2 semi-bold">Payment Details</h3>
            <CheckoutForm cash={watchPaymentType === "cash"} />
          </>
        )}
        <br />
        <Controller
          name="terms"
          render={({ field: { value, onChange, ...field }, fieldState }) => (
            <Checkbox
              id="terms"
              style={fieldState.error ? { color: "#ff0000" } : {}}
              value={value}
              checked={!!value}
              onChange={onChange}
              {...field}
            >
              I agree to the{" "}
              <a
                href="https://heyhomero.com/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms of service
              </a>
            </Checkbox>
          )}
          control={control}
        />
        <br />
        {formState.errors["terms"] && (
          <span style={{ color: "red" }}>
            {formState.errors["terms"].message}
          </span>
        )}
        <br /> <br />
        <PriceNotice
          notice={state.priceNotice}
          currentTotalCents={
            state.priceNotice ? computeBookingPrice(state).totalCents : 0
          }
          pending={state.proximityPending}
          onRepick={() => {
            dispatch(setPriceNotice(null));
            props.changePage(2);
          }}
        />
        <div className="button-group w-full">
          {width < 768 ? (
            <Button
              disabled={!stripe || status === "booking"}
              type="primary"
              htmlType="submit"
              className="next-button first"
            >
              {status === "booking" ? "Please wait..." : "Book Appointment"}
            </Button>
          ) : null}
          <Button
            onClick={() => props.changePage(3)}
            type="primary"
            disabled={status === "booking"}
            className="next-button back-button second"
          >
            Back
          </Button>
          {width >= 768 ? (
            <Button
              disabled={!stripe || status === "booking"}
              htmlType="submit"
              type="primary"
              className="next-button first"
            >
              {status === "booking" ? "Please wait..." : "Book Appointment"}
            </Button>
          ) : null}
        </div>
      </form>
      <br />
    </Layout>
  );
};

export default Payments;
